export const ATTEMPT_FSM_VERSION = 1;

export const AttemptStates = Object.freeze({
  INTAKE: "intake",
  OCR_PENDING: "ocr_pending",
  GATE_OPEN: "gate_open",
  ATTEMPT_ACTIVE: "attempt_active",
  HINT_LEVEL_1: "hint_level_1",
  HINT_LEVEL_2: "hint_level_2",
  HINT_LEVEL_3: "hint_level_3",
  HINT_LEVEL_4: "hint_level_4",
  HINT_LEVEL_5: "hint_level_5",
  COMMIT_OPEN: "commit_open",
  REVIEW: "review",
  COMPLETED: "completed",
});

export const AttemptEventTypes = Object.freeze({
  OCR_REQUESTED: "OCR_REQUESTED",
  OCR_SUCCEEDED: "OCR_SUCCEEDED",
  OCR_FAILED: "OCR_FAILED",
  GATE_OPENED: "GATE_OPENED",
  ATTEMPT_STARTED: "ATTEMPT_STARTED",
  HINT_UNLOCKED: "HINT_UNLOCKED",
  COMMIT_OPENED: "COMMIT_OPENED",
  REVIEW_OPENED: "REVIEW_OPENED",
  COMPLETED: "COMPLETED",
});

const hintStateForLevel = (level) => {
  switch (Number(level)) {
    case 1:
      return AttemptStates.HINT_LEVEL_1;
    case 2:
      return AttemptStates.HINT_LEVEL_2;
    case 3:
      return AttemptStates.HINT_LEVEL_3;
    case 4:
      return AttemptStates.HINT_LEVEL_4;
    case 5:
      return AttemptStates.HINT_LEVEL_5;
    default:
      return null;
  }
};

export const isHintState = (state) =>
  [
    AttemptStates.HINT_LEVEL_1,
    AttemptStates.HINT_LEVEL_2,
    AttemptStates.HINT_LEVEL_3,
    AttemptStates.HINT_LEVEL_4,
    AttemptStates.HINT_LEVEL_5,
  ].includes(state);

export const createAttemptSnapshot = ({
  attemptId = null,
  taskId = null,
  problemId = null,
  source = "unknown",
  nowMs = Date.now(),
  state = AttemptStates.INTAKE,
} = {}) => ({
  id: attemptId,
  taskId,
  problemId,
  source,
  fsmVersion: ATTEMPT_FSM_VERSION,
  state,
  createdAt: nowMs,
  updatedAt: nowMs,
  hintLevel: 0,
});

export const applyAttemptEvent = (attempt, event) => {
  const current = attempt || createAttemptSnapshot();
  const nowMs = Number.isFinite(event?.nowMs) ? event.nowMs : Date.now();
  const type = event?.type;

  const transition = (nextState, patch = {}) => ({
    ok: true,
    from: current.state,
    to: nextState,
    type,
    nowMs,
    attempt: {
      ...current,
      ...patch,
      state: nextState,
      updatedAt: nowMs,
      hintLevel: typeof patch.hintLevel === "number" ? patch.hintLevel : current.hintLevel,
    },
  });

  if (!type) {
    return { ok: false, attempt: current, error: "Missing event type." };
  }

  if (current.state === AttemptStates.COMPLETED) {
    return { ok: false, attempt: current, error: "Attempt is already completed." };
  }

  switch (type) {
    case AttemptEventTypes.OCR_REQUESTED: {
      if (current.state !== AttemptStates.INTAKE) {
        return { ok: false, attempt: current, error: "OCR_REQUESTED only valid from intake." };
      }
      return transition(AttemptStates.OCR_PENDING);
    }

    case AttemptEventTypes.OCR_SUCCEEDED:
    case AttemptEventTypes.OCR_FAILED: {
      if (current.state !== AttemptStates.OCR_PENDING) {
        return { ok: false, attempt: current, error: `${type} only valid from ocr_pending.` };
      }
      return transition(AttemptStates.GATE_OPEN);
    }

    case AttemptEventTypes.GATE_OPENED: {
      if (![AttemptStates.INTAKE, AttemptStates.OCR_PENDING].includes(current.state)) {
        return { ok: false, attempt: current, error: "GATE_OPENED only valid from intake/ocr_pending." };
      }
      return transition(AttemptStates.GATE_OPEN);
    }

    case AttemptEventTypes.ATTEMPT_STARTED: {
      if (![AttemptStates.GATE_OPEN, AttemptStates.INTAKE].includes(current.state)) {
        return { ok: false, attempt: current, error: "ATTEMPT_STARTED only valid from gate_open/intake." };
      }
      return transition(AttemptStates.ATTEMPT_ACTIVE);
    }

    case AttemptEventTypes.HINT_UNLOCKED: {
      const level = Number(event?.level);
      if (!Number.isInteger(level) || level < 1 || level > 5) {
        return { ok: false, attempt: current, error: "HINT_UNLOCKED requires level 1-5." };
      }
      if (![AttemptStates.ATTEMPT_ACTIVE, ...Object.values(AttemptStates).filter(isHintState)].includes(current.state)) {
        // We allow unlocking hints after start only (no implicit start here).
        return { ok: false, attempt: current, error: "HINT_UNLOCKED only valid after attempt_active." };
      }
      const nextHintState = hintStateForLevel(level);
      const nextHintLevel = Math.max(current.hintLevel || 0, level);
      return transition(nextHintState || current.state, { hintLevel: nextHintLevel });
    }

    case AttemptEventTypes.COMMIT_OPENED: {
      if (
        ![AttemptStates.ATTEMPT_ACTIVE, ...Object.values(AttemptStates).filter(isHintState)].includes(
          current.state
        )
      ) {
        return { ok: false, attempt: current, error: "COMMIT_OPENED only valid after attempt_active." };
      }
      return transition(AttemptStates.COMMIT_OPEN);
    }

    case AttemptEventTypes.REVIEW_OPENED: {
      if (![AttemptStates.COMMIT_OPEN, AttemptStates.COMPLETED].includes(current.state)) {
        return { ok: false, attempt: current, error: "REVIEW_OPENED only valid from commit_open/completed." };
      }
      return transition(AttemptStates.REVIEW);
    }

    case AttemptEventTypes.COMPLETED: {
      if (
        ![
          AttemptStates.ATTEMPT_ACTIVE,
          AttemptStates.GATE_OPEN,
          AttemptStates.COMMIT_OPEN,
          AttemptStates.REVIEW,
          ...Object.values(AttemptStates).filter(isHintState),
        ].includes(current.state)
      ) {
        return { ok: false, attempt: current, error: "COMPLETED only valid after gate_open." };
      }
      return transition(AttemptStates.COMPLETED);
    }

    default:
      return { ok: false, attempt: current, error: `Unknown event type: ${type}` };
  }
};

export const deriveBreakLocked = ({
  isRunning = false,
  isPrimary = true,
  tilDone = false,
} = {}) => Boolean(isRunning && !tilDone && !isPrimary);

export const createAttemptClock = ({ nowMs = Date.now(), running = true } = {}) => ({
  accumulatedMs: 0,
  runningSinceMs: running ? nowMs : null,
});

export const setClockRunning = (clock, { nowMs = Date.now(), running = true } = {}) => {
  const current = clock || createAttemptClock({ nowMs, running: false });
  if (running) {
    if (current.runningSinceMs != null) return current;
    return { ...current, runningSinceMs: nowMs };
  }
  if (current.runningSinceMs == null) return current;
  return {
    ...current,
    accumulatedMs: current.accumulatedMs + Math.max(0, nowMs - current.runningSinceMs),
    runningSinceMs: null,
  };
};

export const getClockElapsedMs = (clock, { nowMs = Date.now() } = {}) => {
  const current = clock || createAttemptClock({ nowMs, running: false });
  const activeDelta =
    current.runningSinceMs == null ? 0 : Math.max(0, nowMs - current.runningSinceMs);
  return current.accumulatedMs + activeDelta;
};

